import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { ExerciseColor } from '@/api/enum/common.enum';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { useFilesUploader, useFileUploader } from '@/hooks/useFileUploader';
import { FileInfo } from '@/api/DTO/common.interface';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageCroppers from '@/common/components/imageCroppers/imageCroppers';
import { Day21Request } from '@/api/DTO/exercise/day21.interface';
import {
	useAddDay21Mutation,
	useUpdateDay21Mutation,
} from '@/api/exercise/day21ApiSlice';

interface Props {
	type: FormType;
	details?: Day21Request;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function Day21InfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [addDay21] = useAddDay21Mutation();
	const [updateDay21] = useUpdateDay21Mutation();
	const { uploadFile } = useFileUploader();
	const { uploadFiles } = useFilesUploader();
	const [coverImageFile, setCoverImageFile] = useState<FileInfo | null>(null);
	const [coverImageHalfFile, setCoverImageHalfFile] = useState<FileInfo | null>(
		null
	);
	const [mediaFile, setMediaFile] = useState<FileInfo | null>(null);
	const [videoFile, setVideoFile] = useState<FileInfo | null>(null);
	const [mediaVideoCoverFile, setMediaVideoCoverFile] =
		useState<FileInfo | null>(null);
	const imageInputRef0 = useRef<HTMLInputElement>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef2 = useRef<HTMLInputElement>(null);
	const imageInputRef3 = useRef<HTMLInputElement>(null);
	const imageInputRef4 = useRef<HTMLInputElement>(null);
	const [coverImageShowCropper, setCoverImageShowCropper] = useState(false);
	const [coverImageHalfShowCropper, setCoverImageHalfShowCropper] =
		useState(false);
	const [coverImageBase, setCoverImageBase] = useState<{
		base64: any;
		name: string;
	} | null>(null);
	const [coverImageHalfBase, setCoverImageHalfBase] = useState<{
		base64: any;
		name: string;
	} | null>(null);
	const { day21Id } = useParams();

	const colorList = [
		{ id: ExerciseColor.AQUA, name: t('day21.AQUA') },
		{ id: ExerciseColor.ORANGE, name: t('day21.ORANGE') },
		{ id: ExerciseColor.CORAL, name: t('day21.CORAL') },
		{ id: ExerciseColor.PINK, name: t('day21.PINK') },
		{ id: ExerciseColor.PURPLE, name: t('day21.PURPLE') },
		{ id: ExerciseColor.LAVENDER, name: t('day21.LAVENDER') },
		{ id: ExerciseColor.BLUE, name: t('day21.BLUE') },
		{ id: ExerciseColor.CX, name: t('meditation.CX') },
		{ id: ExerciseColor.MTR, name: t('meditation.MTR') },
	];

	const mediaType = [
		{ id: 3, name: t('day21.image') },
		{ id: 2, name: t('day21.video') },
	];

	const defaultValues: DefaultValues<Day21Request> = {
		contentZh: details?.contentZh || '',
		contentEn: details?.contentEn || '',
		mediaType: details?.mediaType || '',
	};
	useEffect(() => {
		if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
		if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
		if (Number(details?.color) > -1) methods.setValue('color', details?.color);
		if (details?.coverImage) {
			setCoverImageFile(details.coverImage);
			methods.setValue('coverImage', 'file');
		}
		if (details?.coverImageHalf) {
			setCoverImageHalfFile(details.coverImageHalf);
			methods.setValue('coverImageHalf', 'file');
		}
		if (details?.hasContent) methods.setValue('hasContent', details.hasContent);
		if (details?.contentEn) methods.setValue('contentEn', details.contentEn);
		if (details?.contentZh) methods.setValue('contentZh', details.contentZh);

		if (details?.mediaType) methods.setValue('mediaType', details.mediaType);
		if (details?.mediaType == 2) {
			setVideoFile(details?.media);
			methods.setValue('videoFormFile', details.media?.name || '');
			setMediaVideoCoverFile(details?.introductionMediaVideoCover);
			methods.setValue('mediaVideoCoverFormField', details.media?.name || '');
		}
		if (details?.mediaType == 3) {
			setMediaFile(details?.media);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		coverImage: Yup.string().required(t('errorMessage.pleaseSelect')),
		coverImageHalf: Yup.string().required(t('errorMessage.pleaseSelect')),
		color: Yup.string().required(t('errorMessage.pleaseSelect')),
		mediaVideoCoverFormField: Yup.string()
			.nullable()
			.when('videoFormFile', {
				is: (videoFormFile: string) => videoFormFile,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
	});
	const methods = useForm<Day21Request>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const coverImageField = useController({
		name: 'coverImage',
		control: methods.control,
	});
	const coverImageHalfField = useController({
		name: 'coverImageHalf',
		control: methods.control,
	});
	const mediaVideoCoverField = useController({
		name: 'mediaVideoCoverFormField',
		control: methods.control,
	});

	const watchHasContent = methods.watch('hasContent');
	const watchMediaType = methods.watch('mediaType');

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<Day21Request> = async (data) => {
		const obj: Day21Request = { ...data };
		if (!methods.getValues('hasContent')) obj.hasContent = false;
		if (coverImageFile) obj.coverImage = coverImageFile;
		if (coverImageHalfFile) obj.coverImageHalf = coverImageHalfFile;
		if (methods.getValues('hasContent')) {
			obj.contentEn = methods.getValues('contentEn');
			obj.contentZh = methods.getValues('contentZh');
			if (methods.getValues('mediaType') == 2) {
				obj.mediaVideo = videoFile;
				obj.mediaVideoCover = mediaVideoCoverFile;
			} else {
				delete obj.mediaVideoCover;
			}
			if (methods.getValues('mediaType') == 3) {
				obj.mediaImage = mediaFile;
			}
		} else {
			obj.contentEn = '';
			obj.contentZh = '';
			delete obj.mediaType;
			delete obj.mediaImage;
			delete obj.mediaVideo;
			delete obj.mediaVideoCover;
		}
		const response =
			type == FormType.create
				? await addDay21(obj)
				: await updateDay21({ ...obj, id: day21Id || '' });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createMeditationSuccess')
					: t('snackbar.editMeditationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/exercise-manage/day21-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/exercise-manage/day21-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
		type: string
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			if (type == 'media') {
				setMediaFile(res);
			} else if (type == 'video') {
				setVideoFile(res);
				methods.setValue('videoFormFile', res.name);
			} else if (type == 'introduction') {
				setMediaVideoCoverFile(res);
				methods.setValue('mediaVideoCoverFormField', res.name);
			}
		}
	};

	const handleDeleteFile = (type: string) => {
		if (type == 'coverImage') {
			setCoverImageFile(null);
			methods.setValue('coverImage', '');
			imageInputRef0.current && (imageInputRef0.current.value = '');
		} else if (type == 'coverImageHalf') {
			setCoverImageHalfFile(null);
			methods.setValue('coverImageHalf', '');
			imageInputRef.current && (imageInputRef.current.value = '');
		} else if (type == 'media') {
			setMediaFile(null);
			imageInputRef2.current && (imageInputRef2.current.value = '');
		} else if (type == 'video') {
			setVideoFile(null);
			methods.setValue('videoFormFile', '');
			imageInputRef3.current && (imageInputRef3.current.value = '');
		} else if (type == 'introduction') {
			setMediaVideoCoverFile(null);
			methods.setValue('mediaVideoCoverFormField', '');
			imageInputRef4.current && (imageInputRef4.current.value = '');
		}
	};

	const handleThumbnailChange = async (e: any, type: string) => {
		e.preventDefault();
		let files;
		if (e.dataTransfer) {
			files = e.dataTransfer.files;
		} else if (e.target) {
			files = e.target.files;
		}
		const name = files[0].name;
		const reader = new FileReader();
		reader.onload = () => {
			if (type == 'coverImage') {
				setCoverImageShowCropper(true);
				setCoverImageBase({
					base64: reader?.result,
					name: name,
				});
				imageInputRef0.current && (imageInputRef0.current.value = '');
			}
			if (type == 'coverImageHalf') {
				setCoverImageHalfShowCropper(true);
				setCoverImageHalfBase({
					base64: reader?.result,
					name: name,
				});
				imageInputRef.current && (imageInputRef.current.value = '');
			}
		};
		reader.readAsDataURL(files[0]);
	};
	const handleCropper = async (file?: any, type?: string) => {
		if (type == 'coverImage') setCoverImageShowCropper(false);
		if (type == 'coverImageHalf') setCoverImageHalfShowCropper(false);
		if (!file) return;
		const res = await uploadFiles(file);
		if (res) {
			if (type == 'coverImage') {
				methods.setValue('coverImage', 'file');
				methods.trigger('coverImage');
				setCoverImageFile(res[0]);
			}
			if (type == 'coverImageHalf') {
				methods.setValue('coverImageHalf', 'file');
				methods.trigger('coverImageHalf');
				setCoverImageHalfFile(res[0]);
			}

			enqueueSnackbar(t('snackbar.uploadSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={<Typography variant='body1'>{t('day21.zh')}</Typography>}
								value='zh'
							/>
							<Tab
								label={<Typography variant='body1'>{t('day21.en')}</Typography>}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('day21.title')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('day21.title_')}
											maxLength={100}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDropDownMenu
									control={methods.control}
									name='color'
									label={t('day21.color')}
									options={colorList}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('day21.coverImageAcross')}
								</Typography>
								{coverImageFile && (
									<img
										src={coverImageFile?.url || ''}
										width={500}
										height={192}
										style={{
											objectFit: 'contain',
											marginRight: '8px',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{coverImageFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(e) => {
													handleThumbnailChange(e, 'coverImage');
												}}
											/>
											<ImageCroppers
												image={coverImageBase}
												isOpen={coverImageShowCropper}
												handleCropper={(e: any) => {
													handleCropper(e, 'coverImage');
												}}
												propType={'coverImage'}
												option={[
													{
														zoomTo: 0.5,
														initialAspectRatio: 1,
														viewMode: 1,
														autoCropArea: 1,
														checkOrientation: false,
														guides: true,
														aspectRatio: 1000 / 384,
													},
												]}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(e) => {
													handleThumbnailChange(e, 'coverImage');
												}}
											/>
											<ImageCroppers
												image={coverImageBase}
												isOpen={coverImageShowCropper}
												handleCropper={(e: any) => {
													handleCropper(e, 'coverImage');
												}}
												propType={'coverImage'}
												option={[
													{
														zoomTo: 0.5,
														initialAspectRatio: 1,
														viewMode: 1,
														autoCropArea: 1,
														checkOrientation: false,
														guides: true,
														aspectRatio: 1000 / 384,
													},
												]}
											/>
										</Button>
									)}
									{coverImageFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('coverImage');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{coverImageField.fieldState.error?.message}
								</FormHelperText>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('day21.coverImageHalf')}
								</Typography>
								{coverImageHalfFile && (
									<img
										src={coverImageHalfFile?.url || ''}
										width={110}
										height={110}
										style={{
											objectFit: 'contain',
											marginRight: '8px',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{coverImageHalfFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(e) => {
													handleThumbnailChange(e, 'coverImageHalf');
												}}
											/>
											<ImageCroppers
												image={coverImageHalfBase}
												isOpen={coverImageHalfShowCropper}
												handleCropper={(e: any) => {
													handleCropper(e, 'coverImageHalf');
												}}
												propType={'coverImageHalf'}
												option={[
													{
														zoomTo: 0.5,
														initialAspectRatio: 1,
														viewMode: 1,
														autoCropArea: 1,
														checkOrientation: false,
														guides: true,
														aspectRatio: 1 / 1,
													},
												]}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(e) => {
													handleThumbnailChange(e, 'coverImageHalf');
												}}
											/>
											<ImageCroppers
												image={coverImageHalfBase}
												isOpen={coverImageHalfShowCropper}
												handleCropper={(e: any) => {
													handleCropper(e, 'coverImageHalf');
												}}
												propType={'coverHalfImage'}
												option={[
													{
														zoomTo: 0.5,
														initialAspectRatio: 1,
														viewMode: 1,
														autoCropArea: 1,
														checkOrientation: false,
														guides: true,
														aspectRatio: 1 / 1,
													},
												]}
											/>
										</Button>
									)}
									{coverImageHalfFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('coverImageHalf');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{coverImageHalfField.fieldState.error?.message}
								</FormHelperText>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={12}>
								<SingleCheckbox
									control={methods.control}
									name='hasContent'
									label={t('day21.hasContent')}
								/>
							</Grid>
							{watchHasContent && currentTab == 'zh' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('day21.content')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentZh'
									/>
								</Grid>
							)}
							{watchHasContent && currentTab == 'en' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('day21.content')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentEn'
									/>
								</Grid>
							)}
							{watchHasContent && (
								<Grid item xs={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('day21.mediaType_')}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='mediaType'
										row
										options={mediaType}
									/>
								</Grid>
							)}
							{watchHasContent && watchMediaType == 3 && (
								<Grid item xs={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('day21.mediaImage_')}
									</Typography>
									{mediaFile && (
										<img
											src={mediaFile?.url || ''}
											width={390}
											height={138}
											style={{
												objectFit: 'contain',
											}}
										/>
									)}

									<Box display={'flex'} alignItems={'center'}>
										{mediaFile ? (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<LoopOutlinedIcon />}
											>
												{t('information.changeImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef2}
													onChange={(event) => {
														handleUpload(event, 'media');
													}}
												/>
											</Button>
										) : (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<DriveFolderUploadOutlinedIcon />}
											>
												{t('information.uploadImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef2}
													onChange={(event) => {
														handleUpload(event, 'media');
													}}
												/>
											</Button>
										)}
										{mediaFile && (
											<Button
												component='label'
												sx={{ height: 24, marginLeft: '24px' }}
												color='error'
												endIcon={<DeleteOutlinedIcon />}
												onClick={() => {
													handleDeleteFile('media');
												}}
											>
												{t('information.deleteImage')}
											</Button>
										)}
									</Box>
								</Grid>
							)}
							{watchHasContent && watchMediaType == 2 && (
								<>
									<Grid item xs={6}>
										<AppTextField
											disabled
											control={methods.control}
											name='videoFormFile'
											label={t('day21.mediaVideo_')}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														{videoFile?.url && (
															<IconButton
																type='button'
																onClick={() => handleDeleteFile('video')}
															>
																<CloseIcon />
															</IconButton>
														)}

														<IconButton component='label'>
															<AttachFileIcon />
															<VisuallyHiddenInput
																type='file'
																accept='.mp4'
																onChange={(event) =>
																	handleUpload(event, 'video')
																}
																ref={imageInputRef3}
															/>
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
									</Grid>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('day21.previewImage')}
										</Typography>
										{mediaVideoCoverFile && (
											<img
												src={mediaVideoCoverFile?.url || ''}
												width={390}
												height={138}
												style={{
													objectFit: 'contain',
												}}
											/>
										)}

										<Box display={'flex'} alignItems={'center'}>
											{mediaVideoCoverFile ? (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<LoopOutlinedIcon />}
												>
													{t('information.changeImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef4}
														onChange={(event) => {
															handleUpload(event, 'introduction');
														}}
													/>
												</Button>
											) : (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<DriveFolderUploadOutlinedIcon />}
												>
													{t('information.uploadImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef4}
														onChange={(event) => {
															handleUpload(event, 'introduction');
														}}
													/>
												</Button>
											)}
											{mediaVideoCoverFile && (
												<Button
													component='label'
													sx={{ height: 24, marginLeft: '24px' }}
													color='error'
													endIcon={<DeleteOutlinedIcon />}
													onClick={() => {
														handleDeleteFile('introduction');
													}}
												>
													{t('information.deleteImage')}
												</Button>
											)}
										</Box>
										{methods.getValues('videoFormFile') &&
											!methods.getValues('mediaVideoCoverFormField') && (
												<FormHelperText
													color='error'
													style={{ color: '#d32f2f' }}
												>
													{mediaVideoCoverField.fieldState.error?.message}
												</FormHelperText>
											)}
									</Grid>
								</>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
